import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Badge, Card, Spinner } from "react-bootstrap";
import { FaEye, FaPrint, FaDownload, FaBox, FaExclamationTriangle, FaFileAlt,FaFilter } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import "./CustomerList.css";
import * as XLSX from 'xlsx';
import api from "../services/api";

const OrderStatus = () => {
  const [filters, setFilters] = useState({
      startDate: "",
      endDate: "",
      transactionType: "all",
      name: ""
    });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Format ID with leading zeros
  const formatOrderId = (id) => {
    return `ORD-${String(id).padStart(3, '0')}`;
  };

  // Fetch orders from API
  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.post("/order_report_list.php", {});
      
      if (response.data.head.code === 200) {
        // Transform the data
        // const transformedData = response.data.body.map(order => ({
        //   id: order.id,
        //   orderId: formatOrderId(order.id),
        //   rawId: order.id,
        //   date: order.order_date,
        //   customerName: order.customer_name,
        //   customer_id: order.customer_id,
        //   sub_total: parseFloat(order.sub_total) || 0,
        //   gst_percentage: parseFloat(order.gst_percentage) || 0,
        //   gst_amount: parseFloat(order.gst_amount) || 0,
        //   grand_total: parseFloat(order.grand_total) || 0,
        //   status: order.status,
        //   created_at: order.created_at,
        //   amount: `₹${parseFloat(order.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        // }));
        
        setOrders(response.data.body);
      } else if (response.data.head.code === 606) {
        setOrders([]);
      } else {
        setError(response.data.head.msg || "Failed to fetch orders");
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to load orders. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const processOrder = (order) => {
    return {
      id: order.id,
      orderId: formatOrderId(order.id),
      rawId: order.id,
      date: order.order_date,
      customerId: order.customer_id,
      customerName: order.customer_name || "Loading...",
      orderQty: 0, // Will be updated after fetching details
      deliveryQty: 0, // Will be updated
      pendingQty: 0, // Will be updated
      status: order.status,
      grandTotal: parseFloat(order.grand_total) || 0,
      subTotal: parseFloat(order.sub_total) || 0,
      gstAmount: parseFloat(order.gst_amount) || 0,
      items: []
    };
  };

  // Sample data for demo purposes
  

  // Filter orders based on search, date range, and status
  const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;
  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;
  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const filteredOrders = orders.filter((order) => {
  // 🔍 Search filter
  const matchesSearch =
    !search ||
    order.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
    order.order_id?.toLowerCase().includes(search.toLowerCase());

  // 📅 Date filter (if order date exists)
  const orderDate = order.date
    ? parseDDMMYYYY(order.date)
    : null;

  const from = filters.startDate ? parseYYYYMMDD(filters.startDate) : null;
  const to = filters.endDate ? parseYYYYMMDD(filters.endDate) : null;

  if (to) to.setHours(23, 59, 59, 999);

  const matchesDate =
    !orderDate ||
    ((!from || orderDate >= from) &&
     (!to || orderDate <= to));

  return matchesSearch && matchesDate;
});


  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "pending") return "status-badge status-pending";
    if (s === "processing") return "status-badge status-process";
    if (s === "completed") return "status-badge status-completed";
    if (s === "cancelled") return "status-badge status-rejected";
    if (s === "shipped") return "status-badge status-approved";
    return "status-badge status-default";
  };


  // Calculate summary statistics
  const calculateSummary = () => {
    const totalOrders = filteredOrders.length;
    // const totalOrderQty = filteredOrders.reduce((sum, order) => sum + order.orderQty, 0);
    const totalDeliveryQty = filteredOrders.reduce((sum, order) => sum +  Number(order.delivered_qty), 0);
    const totalPendingQty = filteredOrders.reduce((sum, order) => sum +  Number(order.pending_qty), 0);
    // const totalAmount = filteredOrders.reduce((sum, order) => sum + order.grandTotal, 0);
    
    return {
      totalOrders,
      // totalOrderQty,
      totalDeliveryQty,
      totalPendingQty,
      // totalAmount,
      // completionRate: totalOrderQty > 0 ? Math.round((totalDeliveryQty / totalOrderQty) * 100) : 0
    };
  };

  // Get orders by status count
  const getStatusCounts = () => {
    const counts = {
      Pending: 0,
      Processing: 0,
      Shipped: 0,
      Completed: 0,
      Cancelled: 0
    };
    
    filteredOrders.forEach(order => {
      if (counts.hasOwnProperty(order.status)) {
        counts[order.status]++;
      }
    });
    
    return counts;
  };

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentOrders,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredOrders, 10);


  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearAllFilters = () => {
    setSearch("");
    setStatusFilter("");
    setFromDate("");
    setToDate("");
  };

  const handleRefresh = () => {
    fetchOrders();
  };

  const viewOrderDetails = (orderId) => {
    navigate(`/vieworder/${orderId}`);
  };

  if (loading) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading order status...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="outer-container my-3">
        <div className="alert alert-danger" role="alert">
          {error}
          <button className="btn btn-sm btn-outline-danger ms-3" onClick={fetchOrders}>
            Retry
          </button>
        </div>
      </Container>
    );
  }

  const summary = calculateSummary();
  const statusCounts = getStatusCounts();

//   const totals = filteredTransactions.reduce(
//   (acc, item) => {
//     acc.gst_amount += Number(item.gst_amount) || 0;
//     acc.grand_total += Number(item.grand_total) || 0;
//     return acc;
//   },
//   { gst_amount: 0, grand_total: 0 }
// );

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Order Status & Delivery Tracking</h2>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col xs={6} md={4}>
          <Card className="stat-card text-center border-0 bg-primary text-white">
            <Card.Body>
              <h4 className="mb-1">{summary.totalOrders}</h4>
              <small>Total Orders</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4}>
          <Card className="stat-card text-center border-0 bg-success text-white">
            <Card.Body>
              <h4 className="mb-1">{summary.totalDeliveryQty}</h4>
              <small>Total Delivered</small>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={6} md={4}>
          <Card className="stat-card text-center border-0 bg-info text-white">
            <Card.Body>
              <h4 className="mb-1">{summary.totalPendingQty}</h4>
              <small>Pending Delivery</small>
            </Card.Body>
          </Card>
        </Col>
        {/* <Col xs={6} md={3}>
          <Card className="stat-card text-center border-0 bg-info text-white">
            <Card.Body>
              <h4 className="mb-1">{summary.completionRate}%</h4>
              <small>Completion Rate</small>
            </Card.Body>
          </Card>
        </Col> */}
      </Row>

      {/* Filters Section */}
            <div className="filter-section mb-3 p-3 bg-light rounded shadow-sm">
              <Row className="align-items-center mb-2">
                <Col>
                  <h5 className="mb-0">
                    <FaFilter className="me-2" />
                    Filters
                  </h5>
                </Col>
              </Row>
      
              <Row className="g-2">
                
                <Col xs={12} md={6} lg={6} className="mb-2 mb-md-0">
                  <div className="position-relative">
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                      placeholder="Search by Customer Name,Order ID"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setSearch("");
                      }}
                    />
                    {/* <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" /> */}
                  </div>
                </Col>
      
              </Row>
            </div>

      

      {/* Orders Table */}
      <div className="table-pagination-wrapper">
        <div className="table-pagination-content">
          <Table bordered responsive className="table-striped customer-table">
            <thead className="table-dark">
              <tr>
                <th width="5%">S.No</th>
                <th width="10%">Order ID</th>
                <th width="20%">Customer Name</th>
                {/* <th width="10%">Order Date</th> */}
                <th width="10%" className="text-center">Order Qty</th>
                <th width="10%" className="text-center">Delivery Qty</th>
                <th width="10%" className="text-center">Pending Qty</th>
                {/* <th width="10%">Status</th> */}
              </tr>
            </thead>
            <tbody>
              {currentOrders.map((order, index) => {
                
                return (
                  <tr key={order.id}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <FaFileAlt className="text-primary me-2" />
                        {order.order_id}
                      </div>
                    </td>
                    <td>
                      <strong>{order.customer_name}</strong>
                    </td>
                    {/* <td>{order.date}</td> */}
                    <td className="text-center">
                      <Badge bg="primary" className="fs-6">{order.quantity}</Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="success" className="fs-6">{order.delivered_qty}</Badge>
                    </td>
                    <td className="text-center">
                      <Badge bg="warning" className="fs-6">{order.pending_qty}</Badge>
                    </td>
                    {/* <td>
                      <span className={getStatusClass(order.status)}>
                        {order.status}
                      </span>
                    </td> */}
                  </tr>
                );
              })}
              {currentOrders.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center py-4">
                    <div className="text-muted">
                      <FaBox size={48} className="mb-3" />
                      <p>No orders found</p>
                      <small>Try adjusting your search or filters</small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

    </Container>
  );
};

export default OrderStatus;